import { AppliedChemical } from '../data-models/applied-chemical.model';
import { ApplicationTypeInfo } from '../data-models/application-type-info.model';
import { ChemicalUsage } from '../data-models/chemical-usage.model';
import { DroneFlightEntry } from '../data-models/drone-flight-entry.model';
import { EmergencyContactInfo } from '../data-models/emergency-contact-info.model';
import { FlightPathData } from '../data-models/flight-path-data.model';
import { IncidentInformation } from '../data-models/incident-information.model';
import { LoadMixLocation } from '../data-models/load-mix-location.model';
import { SafetyComplianceInfo } from '../data-models/safety-compliance-info.model';
import { SprayDetails } from '../data-models/spray-details.model';
import { VisualObserverInfo } from '../data-models/visual-observer-info.model';
import { WeatherConditions } from '../data-models/weather-conditions.model';
import { isRecord, recordOrEmpty } from './is-record';
import { countOrZero, idString, listOf, nullableBoolean, nullableNumber, nullableString, nullableStringArray } from './wire-value-utils';

/**
 * Readers for the parts of `GET /spray-records/:id` (hub `SprayRecordService.getWithRelated`): the nested groups the
 * hub builds from the record's columns, and its chemicals and drone flights. A value the hub didn't send stays
 * `undefined`; NOT NULL identity columns (ids, names, times) read as `''` when missing.
 */

export function toWeatherConditions(value: unknown): WeatherConditions {
  const source = recordOrEmpty(value);
  return {
    temperature: nullableNumber(source['temperature']),
    humidity: nullableNumber(source['humidity']),
    windSpeed: nullableNumber(source['windSpeed']),
    windDirection: nullableString(source['windDirection']),
    conditions: nullableString(source['conditions']),
  };
}

export function toSprayDetails(value: unknown): SprayDetails {
  const source = recordOrEmpty(value);
  return {
    whatWasTreated: nullableString(source['whatWasTreated']),
    targetPest: nullableString(source['targetPest']),
    nozzleDescription: nullableString(source['nozzleDescription']),
    nozzleToTargetDistance: nullableString(source['nozzleToTargetDistance']),
    pumpPressure: nullableString(source['pumpPressure']),
    travelSpeed: nullableString(source['travelSpeed']),
    descriptionOfDriftManagementPractices: nullableString(source['descriptionOfDriftManagementPractices']),
  };
}

export function toIncidentInformation(value: unknown): IncidentInformation {
  const source = recordOrEmpty(value);
  return {
    takeOffOrLandingDamage: nullableBoolean(source['takeOffOrLandingDamage']),
    descriptionOfTakeOffOrLandingDamage: nullableString(source['descriptionOfTakeOffOrLandingDamage']),
    equipmentMalfunction: nullableBoolean(source['equipmentMalfunction']),
    equipmentMalfunctionType: nullableString(source['equipmentMalfunctionType']),
    descriptionOfEquipmentMalfunction: nullableString(source['descriptionOfEquipmentMalfunction']),
    hasAccident: nullableBoolean(source['hasAccident']),
    accidentType: nullableString(source['accidentType']),
    accidentDescription: nullableString(source['accidentDescription']),
    uaLossType: nullableString(source['uaLossType']),
  };
}

export function toSafetyComplianceInfo(value: unknown): SafetyComplianceInfo {
  const source = recordOrEmpty(value);
  return {
    reentryInterval: nullableString(source['reentryInterval']),
    ppeRequired: nullableStringArray(source['ppeRequired']),
    rinseEquipmentPostApplication: nullableString(source['rinseEquipmentPostApplication']),
  };
}

export function toEmergencyContactInfo(value: unknown): EmergencyContactInfo {
  const source = recordOrEmpty(value);
  return {
    emergencyContactNumber: nullableString(source['emergencyContactNumber']),
    stateEmergencyContactNumber: nullableString(source['stateEmergencyContactNumber']),
  };
}

export function toVisualObserverInfo(value: unknown): VisualObserverInfo {
  const source = recordOrEmpty(value);
  return {
    visualObserver: nullableString(source['visualObserver']),
    voTested: nullableBoolean(source['voTested']),
    notam: nullableString(source['notam']),
  };
}

export function toApplicationTypeInfo(value: unknown): ApplicationTypeInfo {
  const source = recordOrEmpty(value);
  return {
    applicationTypes: nullableStringArray(source['applicationTypes']),
    applicationTypeOther: nullableString(source['applicationTypeOther']),
  };
}

export function toLoadMixLocation(value: unknown): LoadMixLocation {
  const source = recordOrEmpty(value);
  return {
    useLocationForLoadMixLocation: nullableBoolean(source['useLocationForLoadMixLocation']),
    loadMixLocationLatitude: nullableNumber(source['loadMixLocationLatitude']),
    loadMixLocationLongitude: nullableNumber(source['loadMixLocationLongitude']),
  };
}

export function toAppliedChemical(value: unknown): AppliedChemical {
  const source = recordOrEmpty(value);
  return {
    id: idString(source['id']),
    chemicalId: nullableString(source['chemicalId']) ?? undefined,
    name: nullableString(source['name']) ?? '',
    epaNumber: nullableString(source['epaNumber']) ?? null,
    rate: nullableString(source['rate']) ?? '',
    rateValue: nullableNumber(source['rateValue']) ?? null,
    rateUnit: nullableString(source['rateUnit']) ?? null,
    ratePerUnit: nullableString(source['ratePerUnit']) ?? null,
    source: (nullableString(source['source']) ?? 'book') as AppliedChemical['source'],
    templateName: nullableString(source['templateName']),
    category: nullableString(source['category']),
  };
}

export function toChemicalUsage(value: unknown): ChemicalUsage {
  const source = recordOrEmpty(value);
  return {
    appliedChemicalId: idString(source['appliedChemicalId']),
    chemicalName: nullableString(source['chemicalName']) ?? '',
    rate: nullableString(source['rate']) ?? '',
    rateValue: nullableNumber(source['rateValue']),
    rateUnit: nullableString(source['rateUnit']),
    ratePerUnit: nullableString(source['ratePerUnit']),
    amountUsed: nullableString(source['amountUsed']) ?? '',
    amountUsedValue: nullableNumber(source['amountUsedValue']),
    amountUsedUnit: nullableString(source['amountUsedUnit']),
  };
}

export function toDroneFlightEntry(value: unknown): DroneFlightEntry {
  const source = recordOrEmpty(value);
  return {
    id: idString(source['id']),
    droneId: idString(source['droneId']),
    droneSource: source['droneSource'] === 'organization' ? 'organization' : 'adn',
    droneName: nullableString(source['droneName']),
    pilotId: idString(source['pilotId']),
    pilotName: nullableString(source['pilotName']),
    startTime: nullableString(source['startTime']) ?? '',
    endTime: nullableString(source['endTime']) ?? '',
    flightAltitude: nullableNumber(source['flightAltitude']) ?? null,
    flightSpeed: nullableNumber(source['flightSpeed']) ?? null,
    acreageSprayed: countOrZero(source['acreageSprayed']),
    // `null` means "not determined", a different fact from a real 0 (see DroneFlightEntry.numFlights).
    numFlights: nullableNumber(source['numFlights']) ?? null,
    chemicalUsage: listOf(source['chemicalUsage'], toChemicalUsage),
    flightPath: toFlightPathData(source['flightPath']),
  };
}

/** The flight path the form stored (a `FlightPathData` object); `null` for anything else. */
function toFlightPathData(value: unknown): FlightPathData | null {
  return isRecord(value) && Array.isArray(value['coordinates']) ? (value as unknown as FlightPathData) : null;
}
